import React from 'react';
import { Mail, Smartphone, X } from 'lucide-react';
import { useEscapeClose } from '../../../hooks/useEscapeClose';
import {
  buildInvitePermissionLists,
  pruneOverridesToPreset,
  resolveMemberCapabilities,
  type CapabilityMap,
  type PlatformAccess,
} from '../capabilitiesUi';
import type { RolePresetOption } from './RolePresetSelect';
import { UserAccessSettingsFields } from './UserAccessSettingsFields';
import { CapabilityDeviationEditor } from './CapabilityDeviationEditor';
import { useModuleAccess } from '../../../hooks/useModuleAccess';
import { availableRoleEditorPermKeys } from '../roleEditorGroups';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
} from '../utils/settingsSectionStyles';

type MemberRole = 'owner' | 'admin' | 'member';
type EditChannel = 'email' | 'phone';

function isRealMemberEmail(email: string | null | undefined): boolean {
  const value = String(email ?? '').trim();
  return Boolean(value) && !value.endsWith('@phone.invalid');
}

function splitDisplayName(value: string): { firstName: string; lastName: string } {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

export type UserMemberEditModalProps = {
  member: {
    user_id: string;
    display_name: string | null;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    phone?: string | null;
    role: MemberRole;
    platform_access: PlatformAccess;
    role_preset_id?: string | null;
    capability_overrides?: CapabilityMap;
    is_payroll_approver?: boolean;
  };
  presets: RolePresetOption[];
  initials: string;
  avatarClassName: string;
  contactLine: string;
  displayName: string;
  saving: boolean;
  saveError?: string | null;
  /** When true, "Inhaber" appears in the membership dropdown (caller is current owner). */
  canTransferOwnership?: boolean;
  onClose: () => void;
  onSave: (payload: {
    role: MemberRole;
    platform_access: PlatformAccess;
    role_preset_id: string | null;
    capability_overrides: CapabilityMap;
    is_payroll_approver: boolean;
    first_name: string;
    last_name: string;
    display_name: string;
    email?: string;
    phone?: string;
  }) => Promise<void>;
};

const ROLE_OPTIONS = [
  { value: 'member', label: 'Mitglied' },
  { value: 'admin', label: 'Admin' },
];

const OWNER_ROLE_OPTION = { value: 'owner', label: 'Inhaber' };

export function UserMemberEditModal({
  member,
  presets,
  initials,
  avatarClassName,
  contactLine,
  displayName,
  saving,
  saveError = null,
  canTransferOwnership = false,
  onClose,
  onSave,
}: UserMemberEditModalProps) {
  const isOwner = member.role === 'owner';
  // The owner has no role: the baseline is full access, only app rights can deviate from it.
  const ownerBaseCaps = React.useMemo(
    () => resolveMemberCapabilities({ role: 'owner', platformAccess: 'office_and_field' }),
    [],
  );
  const existingRealEmail = isRealMemberEmail(member.email) ? member.email.trim() : '';
  const existingPhone = String(member.phone ?? '').trim();
  const [channel, setChannel] = React.useState<EditChannel>(member.platform_access === 'field_only' ? 'phone' : 'email');
  const [role, setRole] = React.useState<MemberRole>(member.role === 'owner' ? 'owner' : member.role);
  const [access, setAccess] = React.useState<PlatformAccess>(member.platform_access);
  const [presetId, setPresetId] = React.useState(member.role_preset_id ?? '');
  const [overrides, setOverrides] = React.useState<CapabilityMap>(() =>
    pruneOverridesToPreset(
      isOwner
        ? ownerBaseCaps
        : presets.find((preset) => preset.id === (member.role_preset_id ?? ''))?.capabilities ?? {},
      member.capability_overrides ?? {},
    ),
  );
  const [payrollApprover, setPayrollApprover] = React.useState(Boolean(member.is_payroll_approver));
  const [officeEmail, setOfficeEmail] = React.useState(existingRealEmail);
  // Login phone is only editable for members that already are app-only and log in by phone.
  const canEditLoginPhone = member.platform_access === 'field_only' && (Boolean(existingPhone) || !existingRealEmail);
  const [loginPhone, setLoginPhone] = React.useState(existingPhone);
  const initialSplit = splitDisplayName(member.display_name?.trim() || displayName || '');
  const [editedFirstName, setEditedFirstName] = React.useState(
    () => String(member.first_name ?? '').trim() || initialSplit.firstName,
  );
  const [editedLastName, setEditedLastName] = React.useState(
    () => String(member.last_name ?? '').trim() || initialSplit.lastName,
  );
  // Zusammenfassung ohne Bereiche abgewählter Module (wie Rollen & Rechte).
  const { modules } = useModuleAccess();
  const availablePermKeys = React.useMemo(() => availableRoleEditorPermKeys(modules), [modules]);

  useEscapeClose(() => {
    if (!saving) onClose();
  });

  const officePresets = presets.filter((preset) => preset.default_platform_access !== 'field_only');
  const fieldPresets = presets.filter((preset) => preset.default_platform_access === 'field_only');
  const visiblePresets = channel === 'email' ? officePresets : fieldPresets.length ? fieldPresets : presets;
  const selectedPreset = presets.find((preset) => preset.id === presetId) ?? null;
  const presetCaps = selectedPreset?.capabilities ?? {};
  const baseCaps = isOwner ? ownerBaseCaps : presetCaps;

  React.useEffect(() => {
    if (!presetId && visiblePresets.length) {
      setPresetId(visiblePresets[0].id);
    }
  }, [presetId, visiblePresets]);

  React.useEffect(() => {
    if (channel === 'phone') {
      setAccess('field_only');
      return;
    }
    if (selectedPreset) setAccess(selectedPreset.default_platform_access);
  }, [channel, selectedPreset]);

  const effectiveCaps = resolveMemberCapabilities({
    role,
    platformAccess: access,
    presetCapabilities: presetCaps,
    overrideCapabilities: overrides,
  });
  const transferringOwnership = !isOwner && role === 'owner';
  const permissionLists = isOwner || transferringOwnership
    ? {
        web: { items: [{ kind: 'allow' as const, label: 'Vollzugriff auf Büro' }] },
        app: { items: [{ kind: 'allow' as const, label: 'Vollzugriff in der App' }] },
      }
    : buildInvitePermissionLists(effectiveCaps, access, availablePermKeys);

  const handlePresetChange = (nextId: string) => {
    setPresetId(nextId);
    const preset = presets.find((entry) => entry.id === nextId);
    if (preset) setAccess(preset.default_platform_access);
    setOverrides((prev) => pruneOverridesToPreset(preset?.capabilities ?? {}, prev));
  };

  const handleChannelChange = (next: EditChannel) => {
    setChannel(next);
    if (next === 'phone') {
      setRole('member');
      setPayrollApprover(false);
    }
    const pool = next === 'email' ? officePresets : fieldPresets.length ? fieldPresets : presets;
    if (pool.length) setPresetId(pool[0].id);
    setOverrides({});
  };

  const needsOfficeEmail = channel === 'email' && !existingRealEmail;
  const phoneChanged = channel === 'phone' && canEditLoginPhone && loginPhone.trim() !== existingPhone;
  const saveEnabled =
    !saving &&
    Boolean(editedFirstName.trim()) &&
    Boolean(editedLastName.trim()) &&
    (isOwner || transferringOwnership || Boolean(presetId)) &&
    (!needsOfficeEmail || Boolean(officeEmail.trim())) &&
    (!phoneChanged || Boolean(loginPhone.trim()));

  const membershipRoleOptions =
    channel === 'phone'
      ? ROLE_OPTIONS.filter((option) => option.value === 'member')
      : canTransferOwnership
        ? [...ROLE_OPTIONS, OWNER_ROLE_OPTION]
        : ROLE_OPTIONS;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={() => !saving && onClose()}
      role="presentation"
    >
      <div
        className="flex max-h-[92vh] w-full max-w-[52.5rem] flex-col overflow-hidden rounded-xl border border-app-border bg-app-surface shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-start justify-between gap-3 border-b border-app-border px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${avatarClassName}`}>
              {initials}
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-base font-bold text-app-text">
                {[editedFirstName, editedLastName].filter((part) => part.trim()).join(' ').trim() ||
                  displayName ||
                  contactLine ||
                  'Nutzer'}
              </h2>
              {contactLine ? <p className="truncate text-sm text-app-text-subtle">{contactLine}</p> : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-app-text-subtle hover:bg-app-hover"
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto app-scrollbar px-5 py-4">
          {saveError ? (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{saveError}</p>
          ) : null}

          {!isOwner ? (
            <>
              <div className="inline-flex w-full rounded-lg border border-app-border bg-app-surface-muted p-0.5">
                <button
                  type="button"
                  onClick={() => handleChannelChange('email')}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium ${
                    channel === 'email' ? 'bg-app-surface text-pipedrive-blue shadow-sm' : 'text-app-text-subtle'
                  }`}
                >
                  <Mail className="h-4 w-4" />
                  Büro & App
                </button>
                <button
                  type="button"
                  onClick={() => handleChannelChange('phone')}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium ${
                    channel === 'phone' ? 'bg-app-surface text-pipedrive-blue shadow-sm' : 'text-app-text-subtle'
                  }`}
                >
                  <Smartphone className="h-4 w-4" />
                  Nur App
                </button>
              </div>

              {channel === 'phone' ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className={`mb-1.5 block ${SETTINGS_FIELD_LABEL}`}>
                        Vorname <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={editedFirstName}
                        onChange={(e) => setEditedFirstName(e.target.value)}
                        required
                        autoComplete="given-name"
                        placeholder="Max"
                        className={SETTINGS_INPUT_CLASS}
                      />
                    </div>
                    <div>
                      <label className={`mb-1.5 block ${SETTINGS_FIELD_LABEL}`}>
                        Nachname <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={editedLastName}
                        onChange={(e) => setEditedLastName(e.target.value)}
                        required
                        autoComplete="family-name"
                        placeholder="Mustermann"
                        className={SETTINGS_INPUT_CLASS}
                      />
                    </div>
                  </div>
                  {canEditLoginPhone ? (
                    <div>
                      <label className={`mb-1.5 block ${SETTINGS_FIELD_LABEL}`}>Telefonnummer für App-Login</label>
                      <input
                        type="tel"
                        value={loginPhone}
                        onChange={(e) => setLoginPhone(e.target.value)}
                        autoComplete="tel"
                        placeholder="0170 1234567"
                        className={SETTINGS_INPUT_CLASS}
                      />
                      {phoneChanged ? (
                        <p className="mt-1.5 text-xs text-app-text-subtle">
                          Nach dem Speichern meldet sich die Person mit der neuen Nummer an. Das bisherige Gerät wird abgemeldet.
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <div>
                      <label className={`mb-1.5 block ${SETTINGS_FIELD_LABEL}`}>
                        {existingPhone ? 'Telefonnummer für App-Login' : 'E-Mail für App-Login'}
                      </label>
                      <input
                        type={existingPhone ? 'tel' : 'email'}
                        value={existingPhone || existingRealEmail || contactLine}
                        readOnly
                        className={`${SETTINGS_INPUT_CLASS} bg-app-surface-muted text-app-text-muted`}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className={`mb-1.5 block ${SETTINGS_FIELD_LABEL}`}>
                        Vorname <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={editedFirstName}
                        onChange={(e) => setEditedFirstName(e.target.value)}
                        required
                        autoComplete="given-name"
                        placeholder="Max"
                        className={SETTINGS_INPUT_CLASS}
                      />
                    </div>
                    <div>
                      <label className={`mb-1.5 block ${SETTINGS_FIELD_LABEL}`}>
                        Nachname <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={editedLastName}
                        onChange={(e) => setEditedLastName(e.target.value)}
                        required
                        autoComplete="family-name"
                        placeholder="Mustermann"
                        className={SETTINGS_INPUT_CLASS}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={`mb-1.5 block ${SETTINGS_FIELD_LABEL}`}>
                      E-Mail-Adresse {needsOfficeEmail ? <span className="text-red-500">*</span> : null}
                    </label>
                    {existingRealEmail ? (
                      <input
                        type="email"
                        value={existingRealEmail}
                        readOnly
                        className={`${SETTINGS_INPUT_CLASS} bg-app-surface-muted text-app-text-muted`}
                      />
                    ) : (
                      <>
                        <input
                          type="email"
                          value={officeEmail}
                          onChange={(e) => setOfficeEmail(e.target.value)}
                          required
                          autoComplete="email"
                          placeholder="kollegin@betrieb.de"
                          className={SETTINGS_INPUT_CLASS}
                        />
                        <p className="mt-1.5 text-xs text-app-text-subtle">
                          Für den Büro-Zugang wird eine E-Mail benötigt. Die Person erhält eine Einladung zum Web-Login.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}

              <UserAccessSettingsFields
                role={role}
                onRoleChange={(value) => setRole(value as MemberRole)}
                roleOptions={membershipRoleOptions}
                showRoleSelect={channel !== 'phone'}
                roleDisabled={channel === 'phone'}
                presets={visiblePresets}
                presetId={presetId}
                onPresetChange={handlePresetChange}
                permissionLists={permissionLists}
                extraContent={
                  transferringOwnership ? (
                    <p className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-800">
                      Beim Speichern wird die Inhaberschaft an diesen Nutzer übertragen. Du wirst danach Admin.
                    </p>
                  ) : (
                    <CapabilityDeviationEditor
                      presetCaps={presetCaps}
                      overrides={overrides}
                      access={access}
                      isAdmin={role === 'admin'}
                      onChange={setOverrides}
                    />
                  )
                }
              />

              {access !== 'field_only' && !transferringOwnership ? (
                <label className="flex items-center gap-2 text-sm text-app-text-muted">
                  <input
                    type="checkbox"
                    checked={payrollApprover}
                    onChange={(e) => setPayrollApprover(e.target.checked)}
                    className="rounded border-app-border-strong text-pipedrive-blue focus:ring-pipedrive-blue/30"
                  />
                  Lohnbuchhaltungs-Freigeber
                </label>
              ) : null}
            </>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={`mb-1.5 block ${SETTINGS_FIELD_LABEL}`}>
                    Vorname <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editedFirstName}
                    onChange={(e) => setEditedFirstName(e.target.value)}
                    required
                    autoComplete="given-name"
                    placeholder="Max"
                    className={SETTINGS_INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className={`mb-1.5 block ${SETTINGS_FIELD_LABEL}`}>
                    Nachname <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editedLastName}
                    onChange={(e) => setEditedLastName(e.target.value)}
                    required
                    autoComplete="family-name"
                    placeholder="Mustermann"
                    className={SETTINGS_INPUT_CLASS}
                  />
                </div>
              </div>
              <p className="text-sm text-app-text-muted">
                Der Inhaber hat im Büro immer Vollzugriff. In der App lassen sich einzelne Rechte abweichend einstellen.
              </p>
              <CapabilityDeviationEditor
                presetCaps={ownerBaseCaps}
                overrides={overrides}
                access="office_and_field"
                appOnly
                onChange={setOverrides}
              />
            </>
          )}

        </div>

        <div className="flex justify-end gap-2 border-t border-app-border px-5 py-4">
          <button type="button" onClick={onClose} disabled={saving} className={SETTINGS_BTN_OUTLINE}>
            Abbrechen
          </button>
          <button
            type="button"
            disabled={!saveEnabled}
            onClick={() => {
              const first = editedFirstName.trim();
              const last = editedLastName.trim();
              void onSave({
                role: isOwner ? 'owner' : role,
                platform_access: access,
                role_preset_id: presetId || null,
                capability_overrides: pruneOverridesToPreset(baseCaps, overrides),
                is_payroll_approver: transferringOwnership ? true : payrollApprover,
                first_name: first,
                last_name: last,
                display_name: [first, last].filter(Boolean).join(' '),
                ...(needsOfficeEmail ? { email: officeEmail.trim() } : {}),
                ...(phoneChanged ? { phone: loginPhone.trim() } : {}),
              });
            }}
            className={SETTINGS_BTN_PRIMARY}
          >
            {saving ? 'Speichern…' : transferringOwnership ? 'Weiter…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
